// Gemini Files API resumable upload over Node's built-in https (not undici).
// undici's strict content-length parser rejects the resumable-upload response
// when it traverses a local HTTP proxy (Clash) with "invalid content-length
// header"; Node's https parser is lenient and works. Only the upload step needs
// this — files.get/delete and generateContent are plain requests the SDK's fetch
// handles fine. The file body is streamed (never fully buffered).

import * as fs from "node:fs";
import * as http from "node:http";
import * as https from "node:https";

import { HttpsProxyAgent } from "https-proxy-agent";

import { LlmTransportError } from "../../llm/types.js";
import { httpError, networkError } from "./failure-mapping.js";
import type { GeminiFile } from "./gemini-video-model.js";

const DEFAULT_BASE_URL = "https://generativelanguage.googleapis.com";

export function geminiUploadStartUrl(baseUrl: string | null | undefined, apiKey: string): URL {
  const root = new URL(baseUrl ?? DEFAULT_BASE_URL);
  root.pathname = `${root.pathname.replace(/\/+$/, "")}/upload/v1beta/files`;
  root.searchParams.set("key", apiKey);
  return root;
}

export function geminiUploadTransferUrl(uploadUrl: string, baseUrl: string | null | undefined): URL {
  const root = new URL(baseUrl ?? DEFAULT_BASE_URL);
  const transfer = new URL(uploadUrl, root);
  if (!baseUrl) return transfer;

  transfer.protocol = root.protocol;
  transfer.host = root.host;
  const rootPath = root.pathname.replace(/\/+$/, "");
  if (rootPath && transfer.pathname !== rootPath && !transfer.pathname.startsWith(`${rootPath}/`)) {
    transfer.pathname = `${rootPath}/${transfer.pathname.replace(/^\/+/, "")}`;
  }
  return transfer;
}

export function redactCredential(value: string, credential: string): string {
  if (!credential) return value;
  let redacted = value.split(credential).join("[REDACTED]");
  const encoded = encodeURIComponent(credential);
  if (encoded !== credential) redacted = redacted.split(encoded).join("[REDACTED]");
  return redacted;
}

function proxyAgent(proxyUrl: string): https.Agent | undefined {
  return proxyUrl ? (new HttpsProxyAgent(proxyUrl) as unknown as https.Agent) : undefined;
}

interface RawResponse { status: number; headers: Record<string, string | string[] | undefined>; body: string }

function request(
  target: URL,
  opts: Omit<https.RequestOptions, "host" | "path" | "protocol">,
  body: Buffer | fs.ReadStream | string | null,
  signal?: AbortSignal,
): Promise<RawResponse> {
  return new Promise((resolve, reject) => {
    const transport = target.protocol === "http:" ? http : https;
    const req = transport.request({
      ...opts,
      agent: target.protocol === "https:" ? opts.agent : undefined,
      protocol: target.protocol,
      hostname: target.hostname,
      port: target.port || undefined,
      path: `${target.pathname}${target.search}`,
    }, (res) => {
      let data = "";
      res.on("data", (c) => (data += c));
      res.on("end", () => resolve({ status: res.statusCode ?? 0, headers: res.headers, body: data }));
    });
    req.on("error", reject);
    if (signal) {
      if (signal.aborted) { req.destroy(new Error("aborted")); return; }
      signal.addEventListener("abort", () => req.destroy(new Error("aborted")), { once: true });
    }
    if (body && typeof (body as fs.ReadStream).pipe === "function") (body as fs.ReadStream).pipe(req);
    else { if (body) req.write(body); req.end(); }
  });
}

function header(h: RawResponse["headers"], name: string): string {
  const v = h[name];
  return Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
}

export async function resumableUpload(params: {
  apiKey: string;
  filePath: string;
  mimeType: string;
  timeoutMs: number;
  proxyUrl?: string;
  baseUrl?: string | null;
  signal?: AbortSignal;
}): Promise<GeminiFile> {
  const size = fs.statSync(params.filePath).size;
  const agent = proxyAgent(params.proxyUrl ?? "");
  const common = { agent, timeout: params.timeoutMs } as const;
  const root = geminiUploadStartUrl(params.baseUrl, params.apiKey);
  const safe = (value: string): string => redactCredential(value, params.apiKey);

  let start: RawResponse;
  try {
    start = await request(root, {
      ...common, method: "POST",
      headers: {
        "X-Goog-Upload-Protocol": "resumable", "X-Goog-Upload-Command": "start",
        "X-Goog-Upload-Header-Content-Length": String(size), "X-Goog-Upload-Header-Content-Type": params.mimeType,
        "Content-Type": "application/json",
      },
    }, JSON.stringify({ file: {} }), params.signal);
  } catch (e) {
    const detail = safe(e instanceof Error ? e.message : String(e));
    throw networkError(e, params.signal, "Gemini upload start failed.", detail, { operation: "upload" });
  }
  const uploadUrl = header(start.headers, "x-goog-upload-url");
  if (start.status < 200 || start.status >= 300) {
    throw httpError(start.status, "Gemini upload start failed.", safe(start.body).slice(0, 300), { operation: "upload" });
  }
  if (!uploadUrl) {
    throw new LlmTransportError("invalid_response", "Gemini upload start returned no upload URL.", {
      details: [safe(start.body).slice(0, 300)],
      context: { operation: "upload", http_status: start.status },
    });
  }

  const u = geminiUploadTransferUrl(uploadUrl, params.baseUrl);
  let up: RawResponse;
  try {
    up = await request(u, {
      ...common, method: "POST",
      headers: { "Content-Length": String(size), "X-Goog-Upload-Offset": "0", "X-Goog-Upload-Command": "upload, finalize" },
    }, fs.createReadStream(params.filePath), params.signal);
  } catch (e) {
    const detail = safe(e instanceof Error ? e.message : String(e));
    throw networkError(e, params.signal, "Gemini upload transfer failed.", detail, { operation: "upload" });
  }
  if (up.status < 200 || up.status >= 300) {
    throw httpError(up.status, "Gemini upload transfer failed.", safe(up.body).slice(0, 300), { operation: "upload" });
  }
  let parsed: { file?: GeminiFile };
  try { parsed = JSON.parse(up.body) as { file?: GeminiFile }; } catch { throw new LlmTransportError("invalid_response", "Gemini upload returned invalid JSON.", { details: [safe(up.body).slice(0, 300)] }); }
  if (!parsed.file) throw new LlmTransportError("invalid_response", "Gemini upload returned no file.", { details: [safe(up.body).slice(0, 300)] });
  return parsed.file;
}
